import React, { useState, useEffect, type FC } from 'react'
import { ActivityGraderProps, QuestionScore } from '../types'
import type {
  FreeResponseContent,
  FreeResponseAttemptAnswers,
} from '@sphere/learning/domain'
import { Button, TextArea, TextInput, Loading } from '@carbon/react'
import { ArrowLeft, ArrowRight, Checkmark } from '@carbon/icons-react'
import { countWords } from '@sphere/shared/util'
import styles from './FreeResponseGrader.module.scss'

export const FreeResponseGrader: FC<
  ActivityGraderProps<FreeResponseContent, FreeResponseAttemptAnswers>
> = ({ content, userAnswers, onGrade, isSubmitting, initialScores }) => {
  const [scores, setScores] = useState<QuestionScore[]>([])
  const [currentIndex, setCurrentIndex] = useState(0)

  // Initialize scores from initial values or defaults
  useEffect(() => {
    if (content?.prompts) {
      if (initialScores && initialScores.length > 0) {
        setScores(initialScores)
      } else {
        const defaultScores: QuestionScore[] = content.prompts.map(prompt => ({
          questionId: prompt.id,
          score: 0,
          maxScore: 1,
          feedback: '',
        }))
        setScores(defaultScores)
      }
    }
  }, [content?.prompts, initialScores])

  const isLastPrompt = currentIndex === (content?.prompts?.length ?? 0) - 1

  const handleScoreChange = (promptId: string, newScore: number) => {
    setScores(prev =>
      prev.map(s =>
        s.questionId === promptId
          ? { ...s, score: Math.max(0, Math.min(newScore, s.maxScore)) }
          : s
      )
    )
  }

  const handleMaxScoreChange = (promptId: string, newMaxScore: number) => {
    setScores(prev =>
      prev.map(s =>
        s.questionId === promptId
          ? {
              ...s,
              maxScore: Math.max(1, newMaxScore),
              score: Math.min(s.score, Math.max(1, newMaxScore)),
            }
          : s
      )
    )
  }

  const handleFeedbackChange = (promptId: string, feedback: string) => {
    setScores(prev =>
      prev.map(s => (s.questionId === promptId ? { ...s, feedback } : s))
    )
  }

  const handleNext = () => {
    if (isLastPrompt) {
      handleSubmit()
    } else {
      setCurrentIndex(prev => prev + 1)
    }
  }

  const handlePrevious = () => {
    if (currentIndex > 0) setCurrentIndex(prev => prev - 1)
  }

  const handleSubmit = () => {
    const totalScore = scores.reduce((sum, s) => sum + s.score, 0)
    const maxScore = scores.reduce((sum, s) => sum + s.maxScore, 0)
    const passed = maxScore > 0 ? totalScore / maxScore >= 0.5 : false

    onGrade({
      scores,
      totalScore,
      maxScore,
      passed,
      feedback: {
        promptFeedback: scores.reduce(
          (acc, s) => {
            if (s.feedback) {
              acc[s.questionId] = s.feedback
            }
            return acc
          },
          {} as Record<string, string>
        ),
      },
    })
  }

  if (!content?.prompts || content.prompts.length === 0) {
    return (
      <div className={styles.emptyState}>
        <h3>Aucun contenu à noter</h3>
        <p>Cette activité ne contient pas de questions.</p>
      </div>
    )
  }

  const currentPrompt = content.prompts[currentIndex]
  if (!currentPrompt) return null

  // Find the user's answer for the current prompt
  const userAnswer = userAnswers?.answers?.find(a => a.id === currentPrompt.id)
  const answerText = userAnswer?.answer || ''
  const wordCount = countWords(answerText)

  const currentScore = scores.find(s => s.questionId === currentPrompt.id)
  const totalScore = scores.reduce((sum, s) => sum + s.score, 0)
  const maxTotalScore = scores.reduce((sum, s) => sum + s.maxScore, 0)

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <span className={styles.progress}>
          Question {currentIndex + 1} / {content.prompts.length}
        </span>
        <div className={styles.totalScore}>
          Total: {totalScore} / {maxTotalScore}
        </div>
      </div>

      <div className={styles.body}>
        <h3 className={styles.instruction}>{currentPrompt.instruction}</h3>

        {currentPrompt.guidelines && currentPrompt.guidelines.length > 0 && (
          <div className={styles.guidelines}>
            <div className={styles.guidelinesTitle}>Critères d'évaluation</div>
            <ul className={styles.guidelinesList}>
              {currentPrompt.guidelines.map((guideline, idx) => (
                <li key={idx}>{guideline}</li>
              ))}
            </ul>
          </div>
        )}

        <div className={styles.answerCard}>
          <div className={styles.answerLabel}>Réponse de l'élève</div>
          {answerText ? (
            <>
              <div className={styles.answerText}>{answerText}</div>
              <div className={styles.wordCount}>
                {wordCount} mot{wordCount !== 1 ? 's' : ''}
              </div>
            </>
          ) : (
            <div className={`${styles.answerText} ${styles.noAnswer}`}>
              Aucune réponse fournie
            </div>
          )}
        </div>

        {currentScore && (
          <div className={styles.gradingSection}>
            <div className={styles.gradingTitle}>
              <Checkmark size={16} />
              Notation
            </div>
            <div className={styles.gradingInputs}>
              <TextInput
                id={`score-${currentPrompt.id}`}
                labelText="Score"
                type="number"
                min={0}
                max={currentScore.maxScore}
                value={currentScore.score.toString()}
                onChange={e =>
                  handleScoreChange(currentPrompt.id, Number(e.target.value))
                }
                className={styles.scoreInput}
                size="md"
              />
              <span className={styles.scoreDivider}>/</span>
              <TextInput
                id={`max-score-${currentPrompt.id}`}
                labelText="Barème"
                type="number"
                min={1}
                value={currentScore.maxScore.toString()}
                onChange={e =>
                  handleMaxScoreChange(currentPrompt.id, Number(e.target.value))
                }
                className={styles.scoreInput}
                size="md"
              />
              <span className={styles.scorePercent}>
                {((currentScore.score / currentScore.maxScore) * 100).toFixed(0)}%
              </span>
            </div>
            <div className={styles.feedbackArea}>
              <TextArea
                id={`feedback-${currentPrompt.id}`}
                labelText="Commentaire (optionnel)"
                placeholder="Ajoutez un commentaire pour l'élève..."
                value={currentScore.feedback || ''}
                onChange={e =>
                  handleFeedbackChange(currentPrompt.id, e.target.value)
                }
                rows={3}
              />
            </div>
          </div>
        )}
      </div>

      <div className={styles.footer}>
        <Button
          onClick={handlePrevious}
          disabled={currentIndex === 0 || isSubmitting}
          kind="tertiary"
          renderIcon={ArrowLeft}
          size="md"
        >
          Précédent
        </Button>
        <Button
          onClick={handleNext}
          disabled={isSubmitting}
          renderIcon={isLastPrompt ? Checkmark : ArrowRight}
          kind={isLastPrompt ? 'primary' : 'ghost'}
          size="md"
        >
          {isSubmitting ? (
            <Loading small withOverlay={false} />
          ) : isLastPrompt ? (
            'Valider la notation'
          ) : (
            'Suivant'
          )}
        </Button>
      </div>
    </div>
  )
}
